import saveStreamZIP from './saveStreamZIP';

/**
 * Helper function to download large files using streaming
 * @param {string} url - The URL to download from
 * @param {object} argument - Fetch arguments (headers, method, body, etc.)
 * @param {string} filename - The filename for the downloaded file
 * @param {object} options - Additional options
 * @param {function} options.progressCallback - Callback for progress updates
 * @param {string} options.mimeType - MIME type for the file (default: 'application/zip')
 * @param {number} options.progressInterval - Progress logging interval in MB (default: 100)
 * @returns {Promise<Response>} - The fetch response
 */
export const downloadLargeFile = async (url, argument, filename, options = {}) => {
    const {
        progressCallback = null,
        mimeType = 'application/zip',
        progressInterval = 100 // Log progress every 100MB
    } = options;

    console.log(`Starting large file download: ${filename}`);
    
    // Use direct fetch for streaming support
    const response = await fetch(url, argument);
    
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    // Check if streaming is supported
    if (!response.body || typeof response.body.getReader !== 'function') {
        // Fallback to regular text response
        console.log('Streaming not supported, falling back to text response');
        const responseText = await response.text();
        await saveStreamZIP(filename, responseText, { mimeType });
        return response;
    }
    
    const reader = response.body.getReader();
    const contentLength = response.headers.get('content-length');
    const total = contentLength ? parseInt(contentLength, 10) : 0;
    
    let receivedLength = 0;
    const chunks = [];
    
    console.log(`File size: ${total > 0 ? Math.round(total / 1024 / 1024) + 'MB' : 'Unknown'}`);
    
    try {
        // Read the stream
        while (true) {
            const { done, value } = await reader.read();
            
            if (done) break;
            
            chunks.push(value);
            receivedLength += value.length;
            
            // Calculate progress
            const progressPercent = total > 0 ? Math.round((receivedLength / total) * 100) : 0;
            const receivedMB = Math.round(receivedLength / 1024 / 1024);
            const totalMB = Math.round(total / 1024 / 1024);
            
            // Log progress based on interval
            if (total > 0 && receivedLength % (progressInterval * 1024 * 1024) < value.length) {
                console.log(`Download progress: ${progressPercent}% (${receivedMB}MB/${totalMB}MB)`);
            }
            
            // Call progress callback if provided
            if (progressCallback && typeof progressCallback === 'function') {
                progressCallback({
                    percentage: progressPercent,
                    loaded: receivedLength,
                    total: total,
                    loadedMB: receivedMB,
                    totalMB: totalMB
                });
            }
        }
        
        // Combine chunks efficiently into Uint8Array
        const allBytes = new Uint8Array(receivedLength);
        let position = 0;
        
        for (const chunk of chunks) {
            allBytes.set(chunk, position);
            position += chunk.length;
        }
        
        console.log(`Download completed: ${filename} (${Math.round(receivedLength / 1024 / 1024)}MB)`);
        
        // Use enhanced saveStreamZIP with binary data and progress callback
        await saveStreamZIP(filename, allBytes, {
            mimeType,
            progressCallback: (progress, processed, total) => {
                if (progress !== null) {
                    console.log(`Processing: ${progress}% (${Math.round(processed / 1024 / 1024)}MB)`);
                }
                
                // Call external progress callback for processing phase
                if (progressCallback && typeof progressCallback === 'function') {
                    progressCallback({
                        phase: 'processing',
                        percentage: progress || 0,
                        processed: processed || 0,
                        total: total || 0
                    });
                }
            }
        });
        
        return response;
        
    } catch (error) {
        console.error('Streaming download failed:', error);
        throw error;
    }
};

/**
 * Helper to determine if file should use streaming based on response headers
 * @param {Response} response - Fetch response object
 * @param {number} thresholdMB - Threshold in MB to use streaming (default: 50MB)
 * @returns {boolean} - Whether to use streaming
 */
export const shouldUseStreaming = (response, thresholdMB = 50) => {
    const contentLength = response.headers.get('content-length');
    if (!contentLength) return true; // Use streaming if size unknown for safety
    
    const sizeInBytes = parseInt(contentLength, 10);
    const sizeInMB = sizeInBytes / (1024 * 1024);
    
    return sizeInMB > thresholdMB;
};

/**
 * Helper to check file size from response headers
 * @param {Response} response - Fetch response object
 * @returns {object} - File size information
 */
export const getFileSizeInfo = (response) => {
    const contentLength = response.headers.get('content-length');
    
    if (!contentLength) {
        return {
            bytes: 0,
            mb: 0,
            unknown: true,
            formatted: 'Unknown size'
        };
    }
    
    const bytes = parseInt(contentLength, 10);
    const mb = Math.round(bytes / 1024 / 1024);
    
    return {
        bytes,
        mb,
        unknown: false,
        formatted: `${mb}MB`
    };
};

/**
 * Unified download function that automatically chooses the best method
 * @param {string} url - The URL to download from
 * @param {object} argument - Fetch arguments
 * @param {string} filename - The filename for download
 * @param {object} options - Options for download
 * @returns {Promise<Response>} - The response object
 */
export const smartDownload = async (url, argument, filename, options = {}) => {
    const {
        fallbackMethod = null, // Fallback function for traditional download
        thresholdMB = 50,
        progressCallback = null,
        mimeType = 'application/zip'
    } = options;

    try {
        // First, make a HEAD request to check file size if possible
        let useStreaming = false;
        
        try {
            const checkResponse = await fetch(url, {
                ...argument,
                method: 'HEAD'
            });
            
            if (checkResponse.ok) {
                const sizeInfo = getFileSizeInfo(checkResponse);
                useStreaming = shouldUseStreaming(checkResponse, thresholdMB);
                
                console.log(`File size detected: ${sizeInfo.formatted}, using ${useStreaming ? 'streaming' : 'traditional'} method`);
            }
        } catch (headError) {
            // If HEAD fails, default to streaming for safety
            console.log('HEAD request failed, defaulting to streaming approach');
            useStreaming = true;
        }
        
        if (useStreaming) {
            // Use streaming download for large files
            console.log('Using streaming download for potentially large file');
            return await downloadLargeFile(url, argument, filename, {
                progressCallback,
                mimeType
            });
        } else {
            // Use traditional method for smaller files
            console.log('Using traditional download method for small file');
            
            if (fallbackMethod && typeof fallbackMethod === 'function') {
                return await fallbackMethod(url, argument, filename);
            } else {
                // Default fallback
                const response = await fetch(url, argument);
                if (response.ok) {
                    const responseText = await response.text();
                    await saveStreamZIP(filename, responseText, { mimeType });
                }
                return response;
            }
        }
        
    } catch (error) {
        console.error('Smart download error:', error);
        throw error;
    }
};

// Export default for backward compatibility
export default {
    downloadLargeFile,
    shouldUseStreaming,
    getFileSizeInfo,
    smartDownload
};